export class ApiError extends Error {
  status: number;
  body: unknown;
  fieldErrors?: Record<string, string>;
  constructor(
    status: number,
    message: string,
    body?: unknown,
    fieldErrors?: Record<string, string>,
  ) {
    super(message);
    this.status = status;
    this.body = body;
    this.fieldErrors = fieldErrors;
  }
}

interface RequestOptions {
  method?: string;
  body?: BodyInit | Record<string, unknown> | unknown[] | null;
  headers?: HeadersInit;
}

async function request<T>(url: string, opts: RequestOptions = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  let body: BodyInit | null | undefined;
  if (
    opts.body !== undefined &&
    opts.body !== null &&
    !(opts.body instanceof FormData) &&
    !(opts.body instanceof Blob) &&
    !(opts.body instanceof ArrayBuffer) &&
    typeof opts.body !== 'string'
  ) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(opts.body);
  } else {
    body = opts.body as BodyInit | null | undefined;
  }
  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body,
    credentials: 'same-origin',
  });
  const text = await res.text();
  let parsed: unknown = undefined;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    let fieldErrors: Record<string, string> | undefined;
    if (parsed && typeof parsed === 'object') {
      if ('error' in parsed) {
        const errVal = (parsed as { error: unknown }).error;
        if (typeof errVal === 'string' && errVal) msg = errVal;
      }
      if ('errors' in parsed) {
        const errsVal = (parsed as { errors: unknown }).errors;
        if (errsVal && typeof errsVal === 'object' && !Array.isArray(errsVal)) {
          const out: Record<string, string> = {};
          for (const [k, v] of Object.entries(errsVal as Record<string, unknown>)) {
            if (typeof v === 'string') out[k] = v;
          }
          if (Object.keys(out).length > 0) {
            fieldErrors = out;
            if (msg === `HTTP ${res.status}`) msg = 'Please correct the highlighted fields.';
          }
        }
      }
    }
    throw new ApiError(res.status, msg, parsed, fieldErrors);
  }
  return parsed as T;
}

export const api = {
  get: <T>(url: string) => request<T>(url, { method: 'GET' }),
  post: <T>(url: string, body?: Record<string, unknown> | unknown[]) =>
    request<T>(url, { method: 'POST', body }),
  put: <T>(url: string, body?: Record<string, unknown> | unknown[]) =>
    request<T>(url, { method: 'PUT', body }),
  patch: <T>(url: string, body?: Record<string, unknown> | unknown[]) =>
    request<T>(url, { method: 'PATCH', body }),
  delete: <T>(url: string) => request<T>(url, { method: 'DELETE' }),
};
